import { deleteRequest, getRequest, postRequest, putRequest } from "../helpers/networkHelper";
import { SchoolClassVisibilities } from "../enums/schoolClasses";
import { MAX_NAME_LENGTH } from "../constants/LimitConstants";

export interface ClassMember {
    id: string;
    name: string;
    schoolClassId: string;
    loginCode: string;
    createdDate?: string;
    updatedDate?: string;
    deletedDate?: string;
}

export interface PublicSchoolClass {
    token: string;
    name: string;
    classVisibility: SchoolClassVisibilities;
    dateInserted?: number;
}

// The backend spells the school class id differently per endpoint (token, classToken, schoolClassId),
// so the request keys below intentionally diverge from the parameter name.
export const getClassByToken = async (schoolClassId: string): Promise<PublicSchoolClass> => {
    const response = await getRequest("aea/classes/", { token: schoolClassId });
    return response.data;
};

export const getMembersForClass = async (schoolClassId: string): Promise<ClassMember[]> => {
    const response = await getRequest("aea/class-members-class/", { classToken: schoolClassId });
    const membersMap: Record<string, ClassMember> = response.data;
    return Object.values(membersMap)
        .filter((member) => !member.deletedDate)
        .sort((a, b) => a.name.localeCompare(b.name));
};

// getMembersForClass drops soft-deleted members, so an all-deleted class counts as empty (like a
// freshly created one). null = lookup failed; callers treat that as "has members".
export const fetchClassMemberCount = async (schoolClassId: string): Promise<number | null> => {
    try {
        const members = await getMembersForClass(schoolClassId);
        return members.length;
    } catch {
        return null;
    }
};

// Last line of defence for the `class_members.name` column limit — the name inputs already cap the
// length, so this only ever trims names that reach here another way (and the backend would answer
// an oversized one with a data-truncation error rather than a usable message).
export const addMemberToClass = async (schoolClassId: string, name: string): Promise<ClassMember> => {
    const response = await postRequest("aea/class-members/", {
        schoolClassId,
        name: name.slice(0, MAX_NAME_LENGTH),
    });
    return response.data;
};

/** Same request as addMemberToClass, sent by the pupil without a teacher session. */
export const registerClassMember = addMemberToClass;

export const loginClassMember = async (schoolClassId: string, loginCode: string): Promise<ClassMember> => {
    const response = await postRequest("aea/class-members-login/", { schoolClassId, loginCode });
    return response.data;
};

export const updateClassMember = async (memberId: string, name: string): Promise<void> => {
    await putRequest("aea/class-members/", { name: name.slice(0, MAX_NAME_LENGTH) }, { member_id: memberId });
};

export const deleteClassMember = async (memberId: string): Promise<void> => {
    await deleteRequest("aea/class-members/", { member_id: memberId });
};

export interface AddMembersResult {
    succeeded: string[];
    failed: string[];
}

export const addMembersToClass = async (schoolClassId: string, names: string[]): Promise<AddMembersResult> => {
    const results = await Promise.allSettled(names.map((name) => addMemberToClass(schoolClassId, name)));
    const succeeded: string[] = [];
    const failed: string[] = [];
    results.forEach((result, i) => {
        if (result.status === "fulfilled") {
            succeeded.push(names[i]);
        } else {
            failed.push(names[i]);
        }
    });
    return { succeeded, failed };
};

export interface TeacherInfo {
    salutation: string;
    lastname: string;
}

interface RawTeacherInfoResponse {
    salutation?: string;
    lastname?: string;
}

export const getClassTeacherInfo = async (schoolClassId: string): Promise<TeacherInfo> => {
    const response = await getRequest("aea/class-teacher-info/", { classToken: schoolClassId });
    const data = response.data as RawTeacherInfoResponse;
    return {
        salutation: (data.salutation ?? "").trim(),
        lastname: (data.lastname ?? "").trim(),
    };
};
